import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Trash2, Clock3 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Dialog from '@radix-ui/react-dialog';
import {
  approveDoctorBlogCategory,
  deleteDoctorBlogCategory,
  listDoctorBlogCategories,
  markDoctorBlogCategoryPending,
  rejectDoctorBlogCategory,
  type DoctorBlogCategory,
  type DoctorBlogCategoryApproval,
} from '../../../services/Writer/doctorBlogCategoryAdminService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

const DoctorBlogCategoriesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [categories, setCategories] = useState<DoctorBlogCategory[]>([]);
  // Show pending + approved by default (not only pending).
  const [statusFilter, setStatusFilter] = useState<DoctorBlogCategoryApproval | ''>('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DoctorBlogCategory | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadCategories = async (approvalStatus: DoctorBlogCategoryApproval | '' = statusFilter) => {
    setLoading(true);
    try {
      const rows = await listDoctorBlogCategories(approvalStatus || undefined);
      setCategories(rows);
    } catch (error: any) {
      setCategories([]);
      toast.error(getApiErrorMessage(error, 'Failed to load doctor blog categories'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const approveCategory = async (category: DoctorBlogCategory) => {
    if (!window.confirm(`Approve category “${category.name}”?`)) return;
    setActionId(category.categoryId);
    try {
      await approveDoctorBlogCategory(category.categoryId);
      toast.success('Category approved');
      await loadCategories(statusFilter);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to approve category'));
    } finally {
      setActionId(null);
    }
  };

  const rejectCategory = async (category: DoctorBlogCategory) => {
    const reason = window.prompt(`Reason for rejecting “${category.name}” (optional):`) ?? null;
    if (reason === null) return;
    setActionId(category.categoryId);
    try {
      await rejectDoctorBlogCategory(category.categoryId, reason);
      toast.success('Category rejected');
      await loadCategories(statusFilter);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to reject category'));
    } finally {
      setActionId(null);
    }
  };

  const markPendingCategory = async (category: DoctorBlogCategory) => {
    if (!window.confirm(`Mark category “${category.name}” as pending?`)) return;
    setActionId(category.categoryId);
    try {
      await markDoctorBlogCategoryPending(category.categoryId);
      toast.success('Category marked as pending');
      await loadCategories(statusFilter);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to mark category as pending'));
    } finally {
      setActionId(null);
    }
  };

  const openDelete = (category: DoctorBlogCategory) => {
    setDeleteTarget(category);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteDoctorBlogCategory(deleteTarget.categoryId);
      toast.success(
        (deleteTarget.blogCount || 0) > 0
          ? `Category deleted. ${deleteTarget.blogCount} blog(s) were also deleted.`
          : 'Category deleted'
      );
      setDeleteOpen(false);
      setDeleteTarget(null);
      await loadCategories(statusFilter);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to delete category'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Doctor Blog Categories
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Review pending and approved categories submitted by doctors.
          </p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter((e.target.value || '') as DoctorBlogCategoryApproval | '')
          }
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="py-12 text-center text-gray-600 dark:text-gray-400">Loading...</div>
        ) : categories.length === 0 ? (
          <div className="py-12 text-center text-gray-600 dark:text-gray-400">
            No doctor blog categories found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-200">
                <tr>
                  <th className="p-3 text-left">Sr No.</th>
                  <th className="p-3 text-left">Category Name</th>
                  <th className="p-3 text-left">Who Added</th>
                  <th className="p-3 text-left">Blogs</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, index) => {
                  const busy = actionId === category.categoryId || deleteLoading;
                  return (
                    <tr
                      key={category.categoryId}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <td className="p-3 text-gray-700 dark:text-gray-300">{index + 1}</td>
                      <td className="p-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </div>
                        {category.approvalStatus === 'rejected' && category.approvalReason ? (
                          <div className="mt-1 text-xs text-red-600 dark:text-red-300">
                            Reason: {category.approvalReason}
                          </div>
                        ) : null}
                      </td>
                      <td className="p-3 text-gray-700 dark:text-gray-300">
                        {category.addedBy?.name &&
                        category.addedBy.name !== category.vendorId
                          ? category.addedBy.name
                          : 'Doctor'}
                      </td>
                      <td className="p-3 text-gray-700 dark:text-gray-300">
                        {category.blogCount ?? 0}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize ${
                            category.approvalStatus === 'approved'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                              : category.approvalStatus === 'rejected'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                          }`}
                        >
                          {category.approvalStatus}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-2 flex-wrap">
                          {category.approvalStatus === 'pending' ? (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => approveCategory(category)}
                                className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-60"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                                Approve
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => rejectCategory(category)}
                                className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
                              >
                                <XCircle className="h-4 w-4" />
                                Reject
                              </button>
                            </>
                          ) : null}

                          {category.approvalStatus === 'approved' ? (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => markPendingCategory(category)}
                                className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60"
                              >
                                <Clock3 className="h-4 w-4" />
                                Mark pending
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => openDelete(category)}
                                className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </button>
                            </>
                          ) : null}

                          {category.approvalStatus === 'rejected' ? (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => approveCategory(category)}
                                className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-60"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                                Approve
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => markPendingCategory(category)}
                                className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60"
                              >
                                <Clock3 className="h-4 w-4" />
                                Mark pending
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => openDelete(category)}
                                className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog.Root
        open={deleteOpen}
        onOpenChange={(open) => {
          if (deleteLoading) return;
          setDeleteOpen(open);
          if (!open) setDeleteTarget(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-gray-900">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              Delete category?
            </Dialog.Title>
            <Dialog.Description className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              {deleteTarget ? (
                <>
                  You are about to delete <strong>“{deleteTarget.name}”</strong>.
                  <br />
                  <br />
                  <span className="font-semibold text-red-700 dark:text-red-300">
                    Warning: all blogs under this category will also be permanently deleted
                    {(deleteTarget.blogCount || 0) > 0
                      ? ` (${deleteTarget.blogCount} blog${
                          deleteTarget.blogCount === 1 ? '' : 's'
                        })`
                      : ''}
                    . This cannot be undone.
                  </span>
                </>
              ) : (
                'This action cannot be undone.'
              )}
            </Dialog.Description>

            <div className="mt-6 flex justify-end gap-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={deleteLoading}
                  className="rounded-lg bg-gray-200 px-4 py-2 text-gray-900 transition hover:bg-gray-300 disabled:opacity-60 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={deleteLoading}
                onClick={confirmDelete}
                className="rounded-lg bg-red-600 px-4 py-2 text-white transition hover:bg-red-700 disabled:opacity-60"
              >
                {deleteLoading ? 'Deleting…' : 'Delete category & blogs'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default DoctorBlogCategoriesPage;
